import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, message as antdMessage, Image } from 'antd';
import { UserOutlined, CopyOutlined, DownOutlined, PlayCircleOutlined, SoundOutlined, AudioFilled, StopOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useChatStore, Message } from '../../../store/chatStore';
import { useAuthStore } from '../../../store/authStore';
import { getMediaUrl } from '../../../utils/request';
import { SystemSettings } from '../../../api/public';
import { speechService } from '../../../api/speech';
import './ChatArea.css';

const MASCOT_BASE = `${process.env.PUBLIC_URL}/mascot`;

export const MijiaAvatar: React.FC<{
  size: number;
  className?: string;
  interactive?: boolean;
  mouseDistance?: number;
  isSpeaking?: boolean;
  expression?: 'calm' | 'smile' | 'laugh' | 'wave';
  autoExpression?: boolean;
}> = ({ size, className, interactive, mouseDistance, isSpeaking, expression: propExpression, autoExpression }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [currentExpression, setCurrentExpression] = useState<'calm' | 'smile' | 'laugh' | 'wave'>(propExpression || 'calm');
  const [isHovered, setIsHovered] = useState(false);

  // 自动变换表情逻辑
  useEffect(() => {
    if (!autoExpression || isSpeaking) return;

    const expressions: ('calm' | 'smile' | 'laugh')[] = ['calm', 'smile', 'laugh'];
    const timer = setInterval(() => {
      const nextExp = expressions[Math.floor(Math.random() * expressions.length)];
      setCurrentExpression(nextExp);
    }, 3000 + Math.random() * 2000); // 3-5秒变换一次

    return () => clearInterval(timer);
  }, [autoExpression, isSpeaking]);

  // 同步外部传入的表情
  useEffect(() => {
    if (propExpression) {
      setCurrentExpression(propExpression);
    }
  }, [propExpression]);

  // 指针跟随：直接写 CSS 变量，避免每帧触发 React 重渲染
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = wrapRef.current;
    if (!el || !interactive) return;
    const rect = el.getBoundingClientRect();
    const px = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const py = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    const maxTilt = size >= 80 ? 16 : 9;
    el.style.setProperty('--rx', `${(0.5 - py) * 2 * maxTilt}deg`);
    el.style.setProperty('--ry', `${(px - 0.5) * 2 * maxTilt}deg`);
    el.style.setProperty('--px', `${px * 100}%`);
    el.style.setProperty('--py', `${py * 100}%`);
  };

  const resetPointer = () => {
    const el = wrapRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--px', '50%');
    el.style.setProperty('--py', '28%');
  };

  // 根据说话状态、鼠标距离、悬停状态选择表情图（透明背景 PNG）
  let avatarName: 'calm' | 'smile' | 'laugh' | 'speaking' | 'wave' = currentExpression;
  if (interactive && mouseDistance !== undefined) {
    if (mouseDistance < 120) avatarName = 'laugh';
    else if (mouseDistance < 500) avatarName = 'smile';
  }
  if (isHovered && !isSpeaking && avatarName !== 'laugh') avatarName = 'smile';
  if (isSpeaking) avatarName = 'speaking';

  return (
    <div
      ref={wrapRef}
      className={`mijia-avatar-3d ${size >= 80 ? 'is-large' : 'is-small'} ${interactive ? 'interactive-avatar' : ''} ${isHovered ? 'is-hovered' : ''} ${className || ''}`}
      style={{ width: size, height: size }}
      onPointerMove={handlePointerMove}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => {
        setIsHovered(false);
        resetPointer();
      }}
      aria-hidden="true"
    >
      <div className="mijia-avatar-glow" />
      <div className="mijia-avatar-echo">
        <img src={`${MASCOT_BASE}/micah-${avatarName}.png`} alt="" draggable={false} />
      </div>
      <div className="mijia-avatar-stage">
        <img
          className="mijia-avatar-img"
          src={`${MASCOT_BASE}/micah-${avatarName}.png`}
          alt="米迦同学"
          draggable={false}
        />
        <span className="mijia-avatar-sheen" />
      </div>
      <div className="mijia-avatar-ground" />
    </div>
  );
};

// 装修行业规范正则表达式
const STANDARD_PATTERNS = [
  { pattern: /GB\s*\d{5}(?:-\d{4})?/gi, label: '国家标准' },
  { pattern: /JGJ\s*\d{3}(?:-\d{4})?/gi, label: '行业标准' },
  { pattern: /防水高度\s*\d+cm/gi, label: '防水规范' },
  { pattern: /\d+cm\s*防水/gi, label: '防水规范' },
  { pattern: /平整度\s*[≤≤]?\s*\d+mm/gi, label: '施工规范' },
  { pattern: /间距\s*\d+mm/gi, label: '施工规范' },
  { pattern: /垂直度\s*[≤≤]?\s*\d+mm/gi, label: '施工规范' },
];

interface ChatAreaProps {
  theme: 'light' | 'dark';
  systemSettings?: SystemSettings | null;
}

const ChatArea: React.FC<ChatAreaProps> = ({ theme, systemSettings }) => {
  const {
    conversations,
    currentConversationId,
    streamingContent,
    isStreaming,
    sidebarLeftCollapsed,
  } = useChatStore();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [animatingAvatarMessageId, setAnimatingAvatarMessageId] = useState<string | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [distanceToWelcome, setDistanceToWelcome] = useState<number>(999);
  const welcomeLogoRef = useRef<HTMLDivElement>(null);
  const avatarAnimTimerRef = useRef<number | null>(null);
  const prevConversationIdRef = useRef<string | null>(null);
  const justSwitchedConversationRef = useRef(false);

  const currentConversation = conversations.find(
    (conv) => conv.id === currentConversationId
  );

  const messages = currentConversation?.messages || [];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // 计算相对于屏幕中心或视口的偏移，用于眼睛转动
      const x = (e.clientX / window.innerWidth - 0.5) * 2; // -1 to 1
      const y = (e.clientY / window.innerHeight - 0.5) * 2; // -1 to 1
      setMousePos({ x, y });
      
      // 更新全局 CSS 变量，让所有 AI 头像都能响应
      document.documentElement.style.setProperty('--mouse-x', x.toString());
      document.documentElement.style.setProperty('--mouse-y', y.toString());

      // 计算到欢迎界面 Logo 的距离
      if (welcomeLogoRef.current) {
        const rect = welcomeLogoRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dist = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));
        setDistanceToWelcome(dist);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (avatarAnimTimerRef.current != null) {
        window.clearTimeout(avatarAnimTimerRef.current);
      }
      speechService.stop();
    };
  }, []);

  const triggerAvatarAnimation = (messageId: string) => {
    setAnimatingAvatarMessageId(messageId);
    if (avatarAnimTimerRef.current != null) {
      window.clearTimeout(avatarAnimTimerRef.current);
    }
    avatarAnimTimerRef.current = window.setTimeout(() => {
      setAnimatingAvatarMessageId((current) => (current === messageId ? null : current));
    }, 950);
  };

  // 优先使用 messagesContainerRef 滚动消息列表
  const getScrollContainer = (): HTMLElement | null => {
    return messagesContainerRef.current;
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const el = getScrollContainer();
    if (!el) return;
    
    if (behavior === 'auto') {
      el.scrollTop = el.scrollHeight;
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior });
    }
    setIsPinnedToBottom(true);
    setShowScrollButton(false);
  };

  // 监听滚动事件，判断是否贴底以及是否显示滚动按钮
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      
      // 贴底判断：距离底部 60px 以内视为贴底
      const atBottom = distanceFromBottom <= 60;
      setIsPinnedToBottom(atBottom);

      // 按钮显示逻辑：
      // 1. 如果在生成中，稍微往上一点点就显示按钮 (阈值 80px)
      // 2. 如果生成结束，往上滚动一段距离再显示 (阈值 150px，调低以增加灵敏度)
      // 3. 到达底部时，按钮必须消失
      const showThreshold = isStreaming ? 80 : 150;
      
      // 额外判断：只有当内容确实超出一屏时才显示按钮
      const isScrollable = el.scrollHeight > el.clientHeight + 20;
      setShowScrollButton(isScrollable && !atBottom && distanceFromBottom > showThreshold);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    
    // 使用 ResizeObserver 监听内容高度变化（处理异步加载的图片、消息渲染等）
    const resizeObserver = new ResizeObserver(() => {
      handleScroll();
      // 如果之前是贴底的，高度增加时继续保持贴底
      if (isPinnedToBottom) {
        scrollToBottom('auto');
      }
    });
    resizeObserver.observe(el);

    // 初始及内容变化后检查一次
    const t = window.setTimeout(handleScroll, 100);
    const t2 = window.setTimeout(handleScroll, 500); // 增加一次延迟检查，应对较慢的渲染
    
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [currentConversationId, messages.length, isStreaming, isPinnedToBottom]); // 增加 isPinnedToBottom 依赖以支持高度变化时的自动跟随

  // 当切换对话（或首次选择对话）时，停止朗读并回到底部
  useEffect(() => {
    if (prevConversationIdRef.current !== currentConversationId) {
      speechService.stop();
      setSpeakingMessageId(null);
      setIsPinnedToBottom(true);
      setShowScrollButton(false);
      
      // 消息列表容器回到底部
      const scrollEl = messagesContainerRef.current;
      if (scrollEl) {
        scrollEl.scrollTop = 0;
        
        // 增加多重延迟检查
        const scrollTimer = setTimeout(() => scrollToBottom('auto'), 100);
        const scrollTimer2 = setTimeout(() => scrollToBottom('auto'), 400);

        return () => {
          clearTimeout(scrollTimer);
          clearTimeout(scrollTimer2);
        };
      }
    }
    prevConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // 当有新消息或流式内容时，自动滚动到底部（仅在当前对话内）
  useEffect(() => {
    // 如果正在流式响应
    if (isStreaming && streamingContent) {
      // 如果之前处于贴底状态，则持续跟随滚动
      if (isPinnedToBottom) {
        scrollToBottom('auto');
      }
    }
  }, [streamingContent, isStreaming]);

  // 当流式状态开始时，强制触底一次，确保用户看到生成开始
  useEffect(() => {
    if (isStreaming) {
      setIsPinnedToBottom(true);
      // 给 DOM 渲染留一点时间
      const timer = setTimeout(() => {
        scrollToBottom('auto');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isStreaming]);

  // 当消息列表变化时，自动处理滚动
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const isUserMessage = lastMessage.role === 'user';

    // 如果是用户发送的新消息，强制锁定并滚动到底部
    if (isUserMessage) {
      setIsPinnedToBottom(true);
      // 使用双重延迟确保在不同设备上都能准确滚动到位
      scrollToBottom('smooth');
      const timer = setTimeout(() => {
        scrollToBottom('smooth');
      }, 100);
      return () => clearTimeout(timer);
    }
    
    // 如果是 AI 的新消息（生成结束），且当前处于贴底状态，则滚动到底部
    if (!isStreaming && isPinnedToBottom) {
      scrollToBottom('smooth');
    }
  }, [messages.length, isStreaming]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      antdMessage.success('已复制到剪贴板');
    } catch (err) {
      antdMessage.error('复制失败');
    }
  };

  const handleToggleSpeech = (messageId: string, content: string) => {
    if (speakingMessageId === messageId) {
      speechService.stop();
      setSpeakingMessageId(null);
    } else {
      setSpeakingMessageId(messageId);
      speechService.speak(content, () => {
        setSpeakingMessageId(null);
      });
    }
  };

  // 高亮装修行业规范 - 处理文本节点
  const highlightTextNode = (text: string): React.ReactNode => {
    if (typeof text !== 'string') return text;
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const matches: Array<{ start: number; end: number; label: string; match: string }> = [];

    // 收集所有匹配
    STANDARD_PATTERNS.forEach(({ pattern, label }) => {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          label,
          match: match[0],
        });
      }
    });

    // 按位置排序
    matches.sort((a, b) => a.start - b.start);

    // 去重重叠的匹配
    const nonOverlapping: typeof matches = [];
    matches.forEach((match) => {
      if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
        nonOverlapping.push(match);
      }
    });

    // 构建React节点
    nonOverlapping.forEach(({ start, end, label, match }) => {
      if (start > lastIndex) {
        parts.push(text.substring(lastIndex, start));
      }
      parts.push(
        <span key={`${start}-${end}`} className="decoration-standard" data-label={label} title={label}>
          {match}
        </span>
      );
      lastIndex = end;
    });

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 1 ? <>{parts}</> : text;
  };

  const renderMessage = (message: Message, index: number) => {
    const isUser = message.role === 'user';
    const isLastAssistant = !isUser && index === messages.length - 1;
    // 如果是最后一条助手消息且正在流式响应，显示流式内容
    const showStreamingContent = isLastAssistant && isStreaming && streamingContent;
    // 如果消息内容为空但正在流式响应，也显示流式内容
    const isEmptyAndStreaming = !isUser && !message.content && isStreaming && streamingContent;
    const isSpeaking = speakingMessageId === message.id;

    return (
      <div key={message.id} className={`message-item ${isUser ? 'user' : 'assistant'} ${isSpeaking ? 'is-speaking' : ''}`}>
        {!isUser && (
          <div className="message-avatar">
            <button
              type="button"
              className={`ai-avatar-btn ${animatingAvatarMessageId === message.id ? 'is-animating' : ''} ${isSpeaking ? 'speaking-wave' : ''}`}
              onClick={() => triggerAvatarAnimation(message.id)}
              aria-label={(systemSettings?.site_name || '米迦同学') + '头像'}
              title="点我一下"
            >
              <span className="ai-avatar">
                {systemSettings?.ai_icon_url ? (
                  <img 
                    src={getMediaUrl(systemSettings.ai_icon_url)} 
                    alt="AI" 
                    style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} 
                  />
                ) : (
                  <MijiaAvatar size={40} interactive={true} isSpeaking={isSpeaking} />
                )}
              </span>
              {isSpeaking && (
                <div className="speaking-indicator-rings">
                  <div className="ring ring-1"></div>
                  <div className="ring ring-2"></div>
                </div>
              )}
            </button>
          </div>
        )}
        <div className="message-content-wrapper">
          <div className="message-header">
            <span className="message-role">{isUser ? user?.username || '用户' : (systemSettings?.site_name || '米迦同学')}</span>
            <div className="message-actions">
              {!isUser && (message.content || streamingContent) && (
                <Button
                  type="text"
                  size="small"
                  icon={isSpeaking ? <StopOutlined style={{ color: '#3B82F6' }} /> : <SoundOutlined />}
                  onClick={() => handleToggleSpeech(message.id, message.content || streamingContent || '')}
                  title={isSpeaking ? "停止朗读" : "朗读"}
                  className={isSpeaking ? 'btn-speaking' : ''}
                />
              )}
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(message.content || streamingContent || '')}
                title="复制"
              />
            </div>
          </div>
          <div className={`message-content ${isUser ? 'user-content' : 'assistant-content'}`}>
            {isUser ? (
              <div className="message-text">{message.content}</div>
            ) : (
              <>
                {/* 显示已保存的消息内容（如果有） */}
                {message.content && !showStreamingContent && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={theme === 'dark' ? vscDarkPlus : vs}
                            language={language}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // 自定义文本渲染以支持高亮
                      text({ children }: any) {
                        if (typeof children === 'string') {
                          return highlightTextNode(children);
                        }
                        return children;
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
                {/* 显示流式内容（实时更新） */}
                {(showStreamingContent || isEmptyAndStreaming) && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={theme === 'dark' ? vscDarkPlus : vs}
                            language={language}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // 自定义文本渲染以支持高亮
                      text({ children }: any) {
                        if (typeof children === 'string') {
                          return highlightTextNode(children);
                        }
                        return children;
                      },
                    }}
                  >
                    {streamingContent}
                  </ReactMarkdown>
                )}
                {/* 显示打字指示器 */}
                {isLastAssistant && isStreaming && (
                  <span className="typing-indicator">▋</span>
                )}

                {/* 显示图片和视频 */}
                {!isUser && (message.images || message.videos) && (
                  <div className="message-media-container">
                    {message.images && (
                      <div className="message-images">
                        <Image.PreviewGroup>
                          {message.images.split(/[\n,]/).filter(url => url.trim()).map((url, idx) => (
                            <Image
                              key={idx}
                              src={getMediaUrl(url.trim())}
                              width={120}
                              height={120}
                              className="message-image-item"
                              style={{ objectFit: 'cover', borderRadius: '8px' }}
                            />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}
                    {message.videos && (
                      <div className="message-videos">
                        {message.videos.split(/[\n,]/).filter(url => url.trim()).map((url, idx) => (
                          <div key={idx} className="video-wrapper">
                            <video
                              src={getMediaUrl(url.trim())}
                              controls
                              className="message-video-item"
                              style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '8px' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <div 
        ref={welcomeLogoRef}
        className={`welcome-logo ${animatingAvatarMessageId === 'welcome' ? 'is-animating' : ''}`}
        onClick={() => triggerAvatarAnimation('welcome')}
        title="点我一下"
      >
        {systemSettings?.ai_icon_url ? (
          <img src={getMediaUrl(systemSettings.ai_icon_url)} alt="AI" className="welcome-avatar-img" />
        ) : (
          <MijiaAvatar size={120} interactive={true} mouseDistance={distanceToWelcome} expression="wave" />
        )}
      </div>
      <h1>
        {systemSettings?.site_name ? `欢迎使用 ${systemSettings.site_name}` : '欢迎使用米迦同学'}
      </h1>
      <p>您的智能装修助手，随时为您解答装修难题</p>
    </div>
  );

  if (!currentConversation) {
    return (
      <div className="chat-area empty">
        {renderEmptyState()}
      </div>
    );
  }

  return (
    <div 
      className="chat-area"
      ref={chatContainerRef}
    >
      <div className={`chat-messages ${messages.length === 0 ? 'centered' : ''}`} ref={messagesContainerRef}>
        {messages.length === 0 ? (
          renderEmptyState()
        ) : (
          messages.map((message, index) => renderMessage(message, index))
        )}
        <div ref={messagesEndRef} />
      </div>

      {showScrollButton && (
        <div className={`scroll-to-bottom-wrapper ${isStreaming ? 'is-streaming' : ''}`}>
          <Button
            className="scroll-to-bottom-btn"
            type="primary"
            shape="round"
            icon={<DownOutlined />}
            onClick={() => {
              setShowScrollButton(false);
              setIsPinnedToBottom(true);
              scrollToBottom('smooth');
            }}
          >
            {isStreaming ? '正在生成新内容...' : '回到底部'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChatArea;
